/**
 * Turning a file the reader picked into something a question can carry.
 *
 * ## Why the browser downscales
 *
 * A phone photo is 3–8 MB, and base64 adds a third on top. The Ask route caps
 * an attachment at 4 000 000 base64 characters — roughly 3 MB of file — so a
 * straight read of a modern camera photo would be refused about half the
 * time, for a picture whose detail the model does not need: 1600px on the
 * long edge is more than enough to read a crack, a label or a panel.
 *
 * So an image is re-encoded before it is sent. It is the one place in this
 * console that modifies what a person chose, which is why it is honest about
 * it: `downscaled` comes back true and the composer says so.
 *
 * A PDF is passed through untouched — there is no lossless way to shrink one
 * here, and a PDF over the cap is refused with its size rather than mangled.
 */

/** What the Ask route accepts. Mirrors `AskLedAttachment` on the backend. */
export interface AskAttachment {
  mediaType: string;
  /** Base64, no data-URI prefix. */
  data: string;
  name: string;
  /** Bytes, after any re-encode — what was actually sent. */
  bytes: number;
  /** The image was re-encoded smaller. The composer says so. */
  downscaled: boolean;
}

/** Kept in step with `ASK_ATTACHMENT_TYPES` on the backend, which is the
 *  authority; this list only decides what the picker offers. */
export const ASK_ATTACHMENT_ACCEPT =
  "image/png,image/jpeg,image/webp,image/gif,application/pdf";

/** The server's cap, in base64 characters. Checked here so a file that cannot
 *  be sent is refused before the upload rather than after. */
export const ASK_ATTACHMENT_MAX_BASE64 = 4_000_000;

/** The long edge an image is reduced to. Enough to read a crack or a label;
 *  far below what a phone camera produces. */
const MAX_EDGE = 1600;

export class AskAttachmentError extends Error {}

/**
 * Read a picked file into an attachment, downscaling an image if it helps.
 *
 * Throws `AskAttachmentError` with a sentence meant for a person — this is
 * called straight from a file input, and every refusal here is something the
 * reader can act on (pick a smaller file, pick a different one).
 */
export async function readAskAttachment(file: File): Promise<AskAttachment> {
  const mediaType = file.type || "application/octet-stream";
  if (!ASK_ATTACHMENT_ACCEPT.split(",").includes(mediaType)) {
    throw new AskAttachmentError(
      "That file type cannot be attached. Images and PDFs only.",
    );
  }

  const direct = await encode(file);
  if (mediaType === "application/pdf" || direct.length <= ASK_ATTACHMENT_MAX_BASE64) {
    if (direct.length > ASK_ATTACHMENT_MAX_BASE64) {
      throw new AskAttachmentError(
        "Attachments must be less than 3 MB.",
      );
    }
    return {
      mediaType,
      data: direct,
      name: file.name,
      bytes: file.size,
      downscaled: false,
    };
  }

  const smaller = await downscaleImage(file);
  if (!smaller || smaller.data.length > ASK_ATTACHMENT_MAX_BASE64) {
    throw new AskAttachmentError(
      "That image is too large to send with a question, even resized. Under 3 MB, please.",
    );
  }
  return { ...smaller, name: file.name, downscaled: true };
}

/** Base64 without the `data:…;base64,` prefix the reader gives us. */
async function encode(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // In 32k chunks: `String.fromCharCode(...bytes)` on a multi-megabyte array
  // overflows the call stack.
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * Re-encode an image at most `MAX_EDGE` on its long side, as JPEG.
 *
 * Returns `null` where the browser cannot do it — no `createImageBitmap`, no
 * canvas, a decode failure. The caller then refuses the file by size, which
 * is the honest outcome: better than sending something the route will reject.
 */
async function downscaleImage(
  file: File,
): Promise<{ mediaType: string; data: string; bytes: number } | null> {
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") {
    return null;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.82),
    );
    if (!blob) return null;
    return {
      mediaType: "image/jpeg",
      data: await encode(blob),
      bytes: blob.size,
    };
  } catch {
    return null;
  }
}
